import React, { useEffect, useRef } from 'react';
type Strand = {
  y: number;
  tilt: number;
  amp: number;
  freq: number;
  speed: number;
  phase: number;
  w: number;
  r: number;
  g: number;
  b: number;
  a: number;
  glow: number;
};
// Tuned for CREAM background (#FBF8F4) — warm coral / navy palette.
// Opacities are ~10% higher than the previous very-light pass.
const STRANDS: Strand[] = [
// BUNDLE A
{
  y: 0.16,
  tilt: -0.09,
  amp: 0.05,
  freq: 0.0026,
  speed: 0.00013,
  phase: 0.0,
  w: 5.5,
  r: 240,
  g: 160,
  b: 130,
  a: 0.24,
  glow: 12
}, {
  y: 0.17,
  tilt: -0.09,
  amp: 0.05,
  freq: 0.0026,
  speed: 0.00013,
  phase: 0.08,
  w: 1.2,
  r: 232,
  g: 140,
  b: 110,
  a: 0.16,
  glow: 3
}, {
  y: 0.19,
  tilt: -0.09,
  amp: 0.052,
  freq: 0.0026,
  speed: 0.00013,
  phase: 0.2,
  w: 0.6,
  r: 240,
  g: 160,
  b: 130,
  a: 0.11,
  glow: 0
}, {
  y: 0.21,
  tilt: -0.09,
  amp: 0.048,
  freq: 0.0026,
  speed: 0.00013,
  phase: 0.35,
  w: 2.2,
  r: 232,
  g: 140,
  b: 110,
  a: 0.2,
  glow: 4
},
// LONE STRAND
{
  y: 0.32,
  tilt: -0.08,
  amp: 0.06,
  freq: 0.002,
  speed: 0.00009,
  phase: 1.5,
  w: 0.7,
  r: 80,
  g: 90,
  b: 120,
  a: 0.09,
  glow: 0
},
// BUNDLE B (center) — pulled out toward the upper/lower thirds and dimmed
// significantly so it never competes with the centered hero content.
{
  y: 0.4,
  tilt: -0.085,
  amp: 0.05,
  freq: 0.003,
  speed: 0.00015,
  phase: 2.2,
  w: 4.5,
  r: 240,
  g: 160,
  b: 130,
  a: 0.13,
  glow: 8
}, {
  y: 0.41,
  tilt: -0.085,
  amp: 0.05,
  freq: 0.003,
  speed: 0.00015,
  phase: 2.3,
  w: 1.0,
  r: 245,
  g: 180,
  b: 150,
  a: 0.09,
  glow: 2
}, {
  y: 0.66,
  tilt: -0.085,
  amp: 0.055,
  freq: 0.003,
  speed: 0.00015,
  phase: 2.6,
  w: 2.4,
  r: 232,
  g: 140,
  b: 110,
  a: 0.11,
  glow: 4
},
// LONE strand
{
  y: 0.74,
  tilt: -0.075,
  amp: 0.055,
  freq: 0.0024,
  speed: 0.00011,
  phase: 3.4,
  w: 0.8,
  r: 245,
  g: 180,
  b: 150,
  a: 0.11,
  glow: 0
},
// BUNDLE C
{
  y: 0.86,
  tilt: -0.08,
  amp: 0.05,
  freq: 0.0028,
  speed: 0.00012,
  phase: 4.0,
  w: 4.0,
  r: 240,
  g: 160,
  b: 130,
  a: 0.2,
  glow: 9
}, {
  y: 0.88,
  tilt: -0.08,
  amp: 0.05,
  freq: 0.0028,
  speed: 0.00012,
  phase: 4.1,
  w: 0.9,
  r: 232,
  g: 140,
  b: 110,
  a: 0.12,
  glow: 0
}];
const CUT_DURATION = 1400; // ms — slicing animation
const HIDDEN_DURATION = 10000; // ms — strands stay gone after the cut
const FADE_IN_DURATION = 800; // ms — strands fade back in after hidden phase
const TOTAL_CYCLE = CUT_DURATION + HIDDEN_DURATION + FADE_IN_DURATION;
const CUT_GAP_GROW_MS = 700; // how fast the gap opens
// Global alpha multiplier — keeps the ribbon atmospheric without re-tuning
// every strand. Lower = calmer center reading area.
const GLOBAL_ALPHA = 0.65;
type DrawOpts = {
  cutProgress: number; // 0 -> 1 over CUT_DURATION; -1 if not cutting
  cutCenterX: number; // x coord (in CSS px) where cut originates
  fadeIn: number; // 0..1 multiplier when strands return; 1 = fully visible
  hidden: boolean; // if true, skip drawing entirely
};
function drawStrand(ctx: CanvasRenderingContext2D, s: Strand, t: number, W: number, H: number, opts: DrawOpts) {
  if (opts.hidden) return;
  const cutting = opts.cutProgress >= 0;
  // Cut animation: gap grows, halves recoil downward and fade.
  const gapProgress = cutting ? Math.min(1, opts.cutProgress * CUT_DURATION / CUT_GAP_GROW_MS) : 0;
  const halfGap = gapProgress * 260; // px on each side
  const droop = cutting ? Math.pow(opts.cutProgress, 1.4) * 90 : 0;
  const recoilFade = (cutting ? 1 - opts.cutProgress * 0.85 : 1) * opts.fadeIn * GLOBAL_ALPHA;
  // Helper to draw a polyline with the strand's standard gradient + glow
  const strokePath = (xStart: number, xEnd: number, yOffset: number) => {
    if (xEnd <= xStart) return;
    ctx.beginPath();
    let first = true;
    for (let x = xStart; x <= xEnd; x += 5) {
      const y = H * s.y + x * s.tilt + Math.sin(x * s.freq + t * s.speed) * H * s.amp + Math.sin(x * s.freq * 0.55 + t * s.speed * 0.72 + s.phase) * H * s.amp * 0.42 + yOffset;
      if (first) {
        ctx.moveTo(x, y);
        first = false;
      } else {
        ctx.lineTo(x, y);
      }
    }
    const a = s.a * recoilFade;
    const grad = ctx.createLinearGradient(0, 0, W, 0);
    grad.addColorStop(0, `rgba(${s.r},${s.g},${s.b},0)`);
    grad.addColorStop(0.06, `rgba(${s.r},${s.g},${s.b},${a * 0.6})`);
    grad.addColorStop(0.2, `rgba(${s.r},${s.g},${s.b},${a})`);
    grad.addColorStop(0.8, `rgba(${s.r},${s.g},${s.b},${a})`);
    grad.addColorStop(0.94, `rgba(${s.r},${s.g},${s.b},${a * 0.6})`);
    grad.addColorStop(1, `rgba(${s.r},${s.g},${s.b},0)`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = s.w;
    ctx.lineCap = 'round';
    if (s.glow > 0) {
      ctx.shadowColor = `rgba(${s.r},${s.g},${s.b},${0.25 * recoilFade})`;
      ctx.shadowBlur = s.glow;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
  };
  if (!cutting) {
    strokePath(-60, W + 60, 0);
    return;
  }
  // Cut: draw left half + right half with a gap, both drooping and fading.
  const cx = opts.cutCenterX;
  const leftEnd = cx - halfGap;
  const rightStart = cx + halfGap;
  strokePath(-60, leftEnd, droop);
  strokePath(rightStart, W + 60, droop);
}
const RibbonStreaks = ({
  cutKey = 0
}: {
  cutKey?: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cutStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (cutKey > 0) cutStartRef.current = performance.now();
  }, [cutKey]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    let W = 0;
    let H = 0;
    let animId = 0;
    const resize = () => {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    const onResize = () => resize();
    window.addEventListener('resize', onResize);
    const tick = (timestamp: number) => {
      ctx.clearRect(0, 0, W, H);
      // Compute phase: cut → hidden → fade-in → normal
      let cutProgress = -1;
      let hidden = false;
      let fadeIn = 1;
      if (cutStartRef.current !== null) {
        const elapsed = timestamp - cutStartRef.current;
        if (elapsed >= TOTAL_CYCLE) {
          cutStartRef.current = null;
        } else if (elapsed < CUT_DURATION) {
          cutProgress = elapsed / CUT_DURATION;
        } else if (elapsed < CUT_DURATION + HIDDEN_DURATION) {
          hidden = true;
        } else {
          const fadeElapsed = elapsed - CUT_DURATION - HIDDEN_DURATION;
          fadeIn = Math.min(1, fadeElapsed / FADE_IN_DURATION);
        }
      }
      const opts: DrawOpts = {
        cutProgress,
        cutCenterX: W / 2,
        fadeIn,
        hidden
      };
      for (const s of STRANDS) drawStrand(ctx, s, timestamp, W, H, opts);
      animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 w-full h-full" style={{
    zIndex: 0
  }} data-id="element-211" />;
};
export default RibbonStreaks;